import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';
import {
    SiteTheme,
    InitDesignAccountInput,
    SiteInitDesignInput,
    SiteThemeDataFragment,
    useEditSiteDesignMutation,
    useGetThemesQuery,
    useSiteInitSiteDesignMutation,
} from '../../generated/types';
import 'swiper/swiper.min.css';
import { ValidationError } from 'yup';
import { enqueueSnackbar } from 'notistack';
import i18next from 'i18next';
import { initWebsiteSchema } from '../../schemas/siteSchema';
import { useQuery } from '../../hooks/use-query';
import SelectThemeBox from '../select-theme/SelectThemeBox';
import { ArrowBack } from '@menuzen/icon/build/outline';
import ZenLoading from '../../components/loading/Loading';

const StepTemplate: FC = () => {
    const history = useHistory();
    const params = useQuery();
    const [initDesign, { loading: isCreatingDesign }] = useSiteInitSiteDesignMutation();
    const [saveDraftDesign, { loading: isSaveDraftDesign }] = useEditSiteDesignMutation();
    const { loading, data } = useGetThemesQuery();

    const handleCreateMenu = async (theme: SiteThemeDataFragment) => {
        const oldAccountId = sessionStorage.getItem('init_account_id');
        if (oldAccountId) {
            handleApplyNewTheme(parseInt(oldAccountId + '', 10), theme);
            return;
        }
        let data: InitDesignAccountInput;
        try {
            data = await initWebsiteSchema.validate({
                name: params.get('name') || '',
                themeId: theme?.id,
            });
        } catch (e: unknown) {
            const validationError = e as ValidationError;
            console.log(e);
            enqueueSnackbar(validationError.message, { variant: 'error' });
            return;
        }
        const inputData: SiteInitDesignInput = {
            account: {
                name: data.name,
            },
            themeId: theme?.id,
        };

        initDesign({
            variables: {
                input: inputData,
            },
            async update(_, { data }) {
                if (data) {
                    if (data.siteInitDesign?.accountId) {
                        sessionStorage.setItem('init_account_id', String(data.siteInitDesign?.accountId));

                        const result = data.siteInitDesign;
                        history.push(`/account/${result.accountId}/site-design/`);
                    }
                }
            },
            onError: (e) => {
                console.log(e);
                enqueueSnackbar(i18next.t('onBoarding:message.initDesign.failed'), { variant: 'error' });
            },
        });
    };

    const handleApplyNewTheme = async (accountId: number, theme: SiteThemeDataFragment) => {
        await saveDraftDesign({
            variables: {
                input: {
                    patch: {
                        themeId: theme.id,
                        designSettings: theme.designSettings,
                    },
                },
            },
            context: {
                headers: {
                    account: accountId,
                },
            },
            onCompleted: () => {
                history.push(`/account/${accountId}/site-design/`);
            },
            onError: (e) => {
                console.log(e);
                enqueueSnackbar(i18next.t('onBoarding:message.initDesign.failed'), { variant: 'error' });
            },
        });
    };

    const onBack = () => {
        history.push(`/getstarted?${params.toString()}`, { replace: true });
    };

    return (
        <div className="p-32 sm:p-60 pb-0">
            <div className={`mb-24`}>
                <div className="w-30 text-primary text-30 leading-none cursor-pointer" onClick={onBack}>
                    <ArrowBack />
                </div>
            </div>
            <h1 className="mb-10 text-25 leading-28 font-bold text-primary">Choose a template</h1>
            <p className="mb-24 text-14 leading-16 text-primary-80 -tracking-2">
                You can easily swap templates later if you change your mind!
            </p>
            {loading ? (
                <div className="flex items-center justify-center absolute top-0 bottom-0 left-0 right-0">
                    <ZenLoading />
                </div>
            ) : (
                data?.siteThemes && (
                    <>
                        <div className="flex flex-col overflow-y-auto">
                            <SelectThemeBox
                                handleCreateMenu={handleCreateMenu}
                                isAddingMenu={isCreatingDesign || isSaveDraftDesign}
                                themes={data.siteThemes as SiteTheme[]}
                            />
                        </div>
                        <div className="fixed bottom-0 left-0 w-full h-60 bg-gradient-white-to-bottom"></div>
                    </>
                )
            )}
        </div>
    );
};
export default StepTemplate;
